import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Target, CheckSquare, Users, Settings, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import NotificationCenter from './NotificationCenter';
import WelcomeTip from './WelcomeTip';

const tabs = [
  { id: 'home', path: '/', icon: Home, label: 'Home', color: 'violet' },
  { id: 'tasks', path: '/tasks', icon: CheckSquare, label: 'Tasks', color: 'emerald' },
  { id: 'content', path: '/content', icon: FileText, label: 'Content', color: 'sky' },
  { id: 'goals', path: '/goals', icon: Target, label: 'Goals', color: 'amber' },
  { id: 'contacts', path: '/contacts', icon: Users, label: 'Contacts', color: 'pink' },
  { id: 'settings', path: '/settings', icon: Settings, label: 'Settings', color: 'orange' },
];

const colorClasses = {
  violet: { active: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  amber: { active: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  emerald: { active: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  sky: { active: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-100 dark:bg-sky-900/30' },
  pink: { active: 'text-pink-600 dark:text-pink-400', bg: 'bg-pink-100 dark:bg-pink-900/30' },
  orange: { active: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30' },
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const currentTab = tabs.find(tab => tab.path === location.pathname) || tabs[0];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <WelcomeTip />
      {/* Top Header with Notification Bell */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between px-4 py-3 max-w-2xl mx-auto">
          <h1 className="text-lg font-outfit font-semibold text-violet-600 dark:text-violet-400">
            Business Bestie
          </h1>
          <NotificationCenter />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-20 overflow-y-auto">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="h-full"
        >
          {children}
        </motion.div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-t border-border pb-safe z-50">
        <div className="flex items-center justify-around px-2 py-2 max-w-lg mx-auto">
          {tabs.map((tab) => {
            const isActive = location.pathname === tab.path;
            const Icon = tab.icon;
            const colors = colorClasses[tab.color];
            
            return (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                data-testid={`nav-${tab.id}`}
                className={`relative flex flex-col items-center justify-center px-3 py-2 rounded-xl transition-all duration-200 ${
                  isActive ? colors.bg : 'hover:bg-muted'
                }`}
              >
                <motion.div
                  animate={{ scale: isActive ? 1.15 : 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                >
                  <Icon 
                    className={`w-5 h-5 ${isActive ? colors.active : 'text-muted-foreground'}`} 
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                </motion.div>
                <span className={`text-[10px] mt-1 font-medium ${isActive ? colors.active : 'text-muted-foreground'}`}>
                  {tab.label}
                </span>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className={`absolute -bottom-1 w-1 h-1 rounded-full ${colors.active.replace('text-', 'bg-')}`}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
